import {
  BrowserAuthErrorCodes,
  InteractionRequiredAuthError,
  InteractionRequiredAuthErrorCodes,
} from '@azure/msal-browser';

/**
 * Bug #24685 — the ONE place that decides "a silent token request failed in a way only an
 * interactive sign-in can fix". (Same module as apps/admin-portal/src/auth/needsInteraction.ts —
 * separate portal apps per Article IV, so each carries its own copy.)
 *
 * Before this, `getAccessToken` recognised only `InteractionRequiredAuthError`. After hours idle the
 * silent renewal does not fail that way: the hidden-iframe renewal against Entra times out and MSAL
 * throws a `BrowserAuthError` with `errorCode: 'timed_out'` (seen in prod App Insights as an
 * unhandled `PromiseRejectionEvent`). It was rethrown, nothing re-authenticated the user, and the
 * portal simply errored.
 *
 * Codes are read from the installed @azure/msal-browser 5.6.3 / @azure/msal-common 16.4.1 constants
 * wherever MSAL exports one. Two codes asked for in the bug are deliberately NOT here:
 * - `monitor_window_timeout` — does not exist in 5.6.3 (`BrowserAuthErrorCodes` has no such member);
 *   the iframe/redirect-bridge timeouts that replaced it all surface as `timed_out`.
 * - `invalid_grant` has no public MSAL constant, so it is the OAuth 2.0 error code as a literal
 *   (RFC 6749 §5.2). Entra returns it when the refresh token is expired or revoked.
 */
const INTERACTION_ERROR_CODES: ReadonlySet<string> = new Set<string>([
  // BrowserAuthError — silent iframe renewal (or redirect bridge) gave up waiting on Entra.
  BrowserAuthErrorCodes.timedOut,
  // MSAL-originated: nothing usable left in the cache.
  InteractionRequiredAuthErrorCodes.noTokensFound,
  InteractionRequiredAuthErrorCodes.refreshTokenExpired,
  // Server-originated (ServerError / InteractionRequiredAuthError).
  InteractionRequiredAuthErrorCodes.interactionRequired,
  InteractionRequiredAuthErrorCodes.loginRequired,
  InteractionRequiredAuthErrorCodes.consentRequired,
  InteractionRequiredAuthErrorCodes.badToken,
  'invalid_grant',
]);

/**
 * True when `error` can only be resolved by an interactive sign-in.
 *
 * Classified by type first (`InteractionRequiredAuthError`, whatever its code), then by the
 * `errorCode` that every MSAL `AuthError` carries — so a `BrowserAuthError` or `ServerError` is
 * recognised without importing every subclass. Anything that is not an object, or carries no
 * recognised code (a network failure, a 5xx, a malformed response), is false: a transient fault
 * must never send the operator to Microsoft's sign-in page.
 *
 * This does NOT decide entitlement. AADSTS50105 ("not assigned") also arrives as an
 * interaction-required error; callers that care check `isNotAssignedError` (./entitlement) first.
 */
export function needsInteraction(error: unknown): boolean {
  if (error instanceof InteractionRequiredAuthError) return true;
  if (error === null || typeof error !== 'object') return false;
  const code = (error as { errorCode?: unknown }).errorCode;
  return typeof code === 'string' && INTERACTION_ERROR_CODES.has(code);
}

/** True when MSAL refused to start an interaction because its in-progress marker is already set. */
export function isInteractionInProgressError(error: unknown): boolean {
  if (error === null || typeof error !== 'object') return false;
  return (error as { errorCode?: unknown }).errorCode === BrowserAuthErrorCodes.interactionInProgress;
}
